import type { CSSProperties } from 'react'
import type { TableProps } from 'antd'
import type { UseScrollConfig } from './helper'
import React, { useEffect, useRef } from 'react'
import { reaction, toJS } from 'mobx'
import { observer, useLocalObservable } from 'mobx-react'
import { Table } from 'antd'
import { useTableScroll } from './helper'
import { get, uniq, uniqWith } from 'lodash'
import 'src/styles/components/common/easyTable.less'

type Payload = {
  PageIndex: number,
  Limit: number,
  [key: string]: any
}

export type State = {
  total: number,
  setTotal: (data: number) => void,
  dataSource: any[],
  setDataSource: (data: any[]) => void,
  selectedKeys: React.Key[],
  setSelectedKeys: (data: React.Key[]) => void,
  payload: Payload,
  setPayload: (data?: Partial<Payload>) => void,
  loadedData: any[],
  setLoadedData: (data: any[]) => void,
  reload: (params?: Partial<Payload>) => void
}

export type Props = {
  onRef?: (ins: State) => void,
  table?: TableProps<any>,
  extraPayload?: { [key: string]: any },
  remoteMethod?: (payload: Payload) => Promise<{ TotalCount: number, Data: any[] }>,
  // 后端返回数据结构取值字段
  remoteField?: {
    total?: string,
    data?: string,
  },
  selectDisabled?: boolean,
  onSelectChange?: (selectedKeys: React.Key[], selectedRows: any[]) => void,
  onLoadedData?: (data: { data: any[] }) => void, // 加载完数据时的回调
  useScroll?: UseScrollConfig,
  className?: string,
  style?: CSSProperties
}

const EasyTable = (props: Props) => {
  const state = useLocalObservable<State>(() => {
    return {
      total: 0,
      setTotal (data) {
        this.total = data
      },
      dataSource: [],
      setDataSource (data) {
        this.dataSource = data
      },
      selectedKeys: [],
      setSelectedKeys (data) {
        this.selectedKeys = data
      },
      payload: { PageIndex: 1, Limit: 10 },
      setPayload (data) {
        this.payload = Object.assign({}, this.payload, data)
      },
      loadedData: [],
      setLoadedData (data) {
        this.loadedData = data
      },
      reload (params) {
        this.setPayload(params)
      }
    }
  })

  useEffect(() => { props.onRef?.(state) }, [])

  useEffect(() => {
    const selected = props.table?.rowSelection?.selectedRowKeys
    selected !== undefined && state.setSelectedKeys(selected)
  }, [props.table?.rowSelection?.selectedRowKeys])

  useEffect(() => {
    state.setPayload({ PageIndex: 1, ...props.extraPayload })
  }, [props.extraPayload])

  useEffect(() => reaction(
    () => state.payload,
    payload => {
      props.remoteMethod?.(payload).then(res => {
        if (res) {
          const { data = 'Data', total = 'TotalCount' } = props.remoteField ?? {}
          const totalCount = get(res, total) ?? 0
          const dataSource = get(res, data) ?? []
          state.setTotal(totalCount)
          state.setDataSource(dataSource)
          state.setLoadedData(uniqWith(
            // uniqWith 注意 concat 顺序，uniqWith 会把数组后面符合条件的给去掉，所以需要最新的在前面
            ([] as any[]).concat(dataSource, state.loadedData),
            (a: any, b: any) => {
              const rowKey = props.table?.rowKey as string || 'ID'
              return a[rowKey] === b[rowKey]
            }
          ))
          // 暴露 onLoadedData 接口
          props.onLoadedData?.({ data: dataSource })
        }
      })
    },
    { fireImmediately: true }
  ), [])

  const handleChange = (...args: any[]) => {
    const [pagination] = args
    state.setPayload({ PageIndex: pagination.current, Limit: pagination.pageSize })
    // @ts-ignore
    props.table?.onChange?.(...args)
  }

  const TRowKey = props.table?.rowKey as string || 'ID'
  const TRowSelection = props.table?.rowSelection
  const checkboxEnable = !TRowSelection?.type || TRowSelection?.type === 'checkbox'
  const rowSelection: TableProps<any>['rowSelection'] = !props.selectDisabled ? {
    fixed: 'left',
    type: 'checkbox',
    columnWidth: 32,
    ...TRowSelection,
    selectedRowKeys: state.selectedKeys,
    // 单/多选
    onSelect: (...args: any[]) => {
      const copyData = toJS(state.selectedKeys)

      const [record, selected] = args
      let newKeys = [record[TRowKey]]
      if (checkboxEnable) {
        if (selected) {
          newKeys = uniq(newKeys.concat(copyData))
        } else {
          newKeys = copyData.filter(key => key !== newKeys[0])
        }
      }
      state.setSelectedKeys(newKeys)
      props.onSelectChange?.(newKeys, state.loadedData.filter((item: any) => newKeys.indexOf(item[TRowKey]) !== -1))
    },
    // 全选
    onSelectAll: (...args: any[]) => {
      const copyData = toJS(state.selectedKeys)

      let [selected, selectedRows, changeRows] = args
      // 这两货竟然会有 undefined 的 item 存在
      selectedRows = selectedRows.filter((row: any) => !!row)
      changeRows = changeRows.filter((row: any) => !!row)
      /**
       * 全选时，selectedRows 肯定是全选的数据
       * 取消全选时，changeRows 肯定是全选的数据
       */
      let newKeys = selected ? selectedRows.map((row: any) => row[TRowKey]) : changeRows.map((row: any) => row[TRowKey])
      if (selected) {
        newKeys = uniq(newKeys.concat(copyData))
      } else {
        newKeys = copyData.filter(key => newKeys.indexOf(key) === -1)
      }
      state.setSelectedKeys(newKeys)
      props.onSelectChange?.(newKeys, state.loadedData.filter((item: any) => newKeys.indexOf(item[TRowKey]) !== -1))
    }
  } : undefined

  const pagination = props.table?.pagination === false ? false : {
    showSizeChanger: true,
    showTotal: (total: number) => `共 ${total} 条`,
    ...props.table?.pagination,
    total: state.total,
    current: state.payload.PageIndex,
    pageSize: state.payload.Limit
  }

  const wrapEl = useRef(null)
  const scroll = useTableScroll(wrapEl, {
    validScroll: state.dataSource.length,
    ...props.useScroll
  })

  return (
    <div ref={wrapEl} className={`easy-table ${props.className}`} style={props.style}>
      <Table
        rowKey='ID'
        size='middle'
        {...props.table}
        scroll={scroll}
        dataSource={state.dataSource}
        rowSelection={rowSelection}
        pagination={pagination}
        onChange={handleChange}
      />
    </div>
  )
}

export type RefIns = State
export default observer(EasyTable)